import {
    AWIN_CHANNEL_COOKIE,
    AWIN_COOKIE_EXPIRY_DAYS,
    AFFILIATE_PARAMS,
} from '../configuration/constants';
import getCookie from '../cookieBanner/getCookie';
import { parseUrlParameters } from '../initialization/parseUrlParameters';

type AffiliateData = Record<string, string>;

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const MIN_PARTS_FOR_SUBDOMAIN = 2;

function getRootDomain(hostname: string): string {
    const parts = hostname.split('.');
    if (parts.length <= MIN_PARTS_FOR_SUBDOMAIN) {
        return hostname;
    }
    return parts.slice(-MIN_PARTS_FOR_SUBDOMAIN).join('.');
}

function setAffiliateCookie(data: AffiliateData, hostname: string) {
    const domain = getRootDomain(hostname);
    const expiryMs =
        AWIN_COOKIE_EXPIRY_DAYS *
        HOURS_PER_DAY *
        MINUTES_PER_HOUR *
        SECONDS_PER_MINUTE *
        MILLISECONDS_PER_SECOND;
    const date = new Date();
    date.setTime(date.getTime() + expiryMs);
    const expires = '; expires=' + date.toUTCString();
    const valueString = encodeURIComponent(JSON.stringify(data));
    document.cookie =
        AWIN_CHANNEL_COOKIE +
        '=' +
        valueString +
        expires +
        '; path=/; domain=.' +
        domain;
}

function removeAffiliateCookie(hostname: string) {
    const domain = getRootDomain(hostname);
    document.cookie =
        AWIN_CHANNEL_COOKIE +
        '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' +
        domain;
}

export function createAffiliateModule(window: Window & typeof globalThis) {
    let affiliateData: AffiliateData = {};

    const init = () => {
        const urlParams = parseUrlParameters(window.location.href);

        const paramsFromUrl: AffiliateData = {};
        for (const param of AFFILIATE_PARAMS) {
            if (urlParams[param]) {
                paramsFromUrl[param] = urlParams[param];
            }
        }

        const hasNewParams = Object.keys(paramsFromUrl).length > 0;

        if (hasNewParams) {
            // Merge with existing cookie data, URL params take precedence
            const existingCookie: AffiliateData =
                getCookie(AWIN_CHANNEL_COOKIE) || {};
            affiliateData = { ...existingCookie, ...paramsFromUrl };
            setAffiliateCookie(affiliateData, window.location.hostname);
        } else {
            // Restore from cookie if no new params in URL
            affiliateData = getCookie(AWIN_CHANNEL_COOKIE) || {};
        }
    };

    const getAffiliateData = (): AffiliateData => ({ ...affiliateData });

    const clearAffiliate = () => {
        affiliateData = {};
        removeAffiliateCookie(window.location.hostname);
    };

    const setAffiliateData = (data: AffiliateData) => {
        affiliateData = { ...affiliateData, ...data };
        setAffiliateCookie(affiliateData, window.location.hostname);
    };

    return {
        init,
        getAffiliateData,
        setAffiliateData,
        clearAffiliate,
    };
}
